/**
 * What this session has in hand, above the composer (RFC 0297 §4d, S5).
 *
 * OPTION C from the design canvas. The bar rests as one line and opens into
 * rows, because the common case is a quiet session and a quiet session should
 * cost one line. B, the card that is always open, spends five lines before you
 * have typed anything. A, chips inside the composer, costs nothing to build and
 * says the wrong thing: everything in that row today goes WITH the message, and
 * a holding does not.
 *
 * IT HAS TO BE BORING WHEN NOTHING HAS HAPPENED. The amber dot is the whole
 * notification, and it means nothing if the resting state has colour in it.
 *
 * SPLIT IN TWO. `HoldingsBar` opens the two documents and `HoldingsBarView`
 * draws rows it is handed. The split is what lets the states be looked at side
 * by side without a repo, which is how the design was settled in the first
 * place, and it keeps the reading in one place rather than in the markup.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo'
import { useRepo } from '@automerge/automerge-repo-react-hooks'
import {
  commentListView,
  type HeldDocument,
  isWaitingOn,
  type Pairing,
  pairingFor,
  pairSession,
  projectPairings,
  projectSessionHeldDocuments,
  projectSessionHoldings,
  projectThreads,
  sessionPairingRows,
  setPairingMayEdit,
  unpairSession,
  usesPrivateSessionCatalog,
} from '@mod/core/model'
import { putDownDocument } from '@mod/core/run'
import { ChevronDown, ChevronUp } from 'lucide-react'
import { type ReactNode, useMemo, useState } from 'react'
import { useCommentMap } from '../../services/comments/useCommentMap'
import { setPendingPassage } from '../../services/documents/pendingPassage'
import { useLocalPersonId } from '../../services/identity/localPerson'
import { type Selection, useSelection } from '../../services/sessions/selection'
import {
  useDocument,
  useDocuments,
} from '../../services/world/repo/useDocument'
import { StatusDot } from '../../shell/window/StatusDot'
import { ago, summary } from './holdingsBarText'

/**
 * How many rows open by default (§4f).
 *
 * Five, then a line saying how many more. A session that refactors forty files
 * holds forty documents, and a bar with forty rows is not a bar.
 */
const SHOWN = 5

/**
 * The bar for one session, with its actions written to the log.
 *
 * ONE COPY OF THE ACTIONS, for every surface that draws a composer. The thread
 * drew the bar with handlers built in ThreadView, and the composer strip
 * (RFC 0284 S8d) is a second composer that had no bar at all: a person typing
 * into it could not see what the session they were messaging had in hand.
 */
export function SessionHoldingsBar({
  sessionId,
  projectId,
  onOpenDocument,
  framed,
  leading,
  trailing,
}: {
  sessionId: string
  projectId?: string
  onOpenDocument?: (documentId: string) => void
  framed?: boolean
  leading?: ReactNode
  trailing?: ReactNode
}) {
  const repo = useRepo() as unknown as Repo
  const actions = useMemo(() => {
    const quiet = () => {}
    return {
      // RFC 0297 4f. On the log rather than in local state: a holding hidden
      // here and still resident in the agent's context is the lie the RFC
      // exists to remove. A drop that did not land leaves the row where it was.
      onPutDown: (documentId: string) =>
        void putDownDocument(repo as never, sessionId, documentId).catch(quiet),
      // RFC 0301 §3a. On the document (RFC 0339 3b), indexed on this
      // project, so every surface reads the same pairing. Absent without a project, which leaves the row without the
      // action rather than with one that has nowhere to write.
      ...(projectId
        ? {
            onPair: (documentId: string) =>
              void pairSession(repo as never, projectId, {
                sessionId,
                documentId,
                createdBy: 'user',
              }).catch(quiet),
            onSetMayEdit: (pairingId: string, mayEdit: boolean) =>
              void setPairingMayEdit(
                repo as never,
                projectId,
                pairingId,
                mayEdit,
              ).catch(quiet),
            onUnpair: (pairingId: string) =>
              void unpairSession(repo as never, projectId, pairingId).catch(
                quiet,
              ),
          }
        : {}),
    }
  }, [repo, sessionId, projectId])
  return (
    <HoldingsBar
      sessionId={sessionId}
      {...(projectId ? { projectId } : {})}
      {...(onOpenDocument ? { onOpenDocument } : {})}
      {...(framed === undefined ? {} : { framed })}
      {...(leading ? { leading } : {})}
      {...(trailing ? { trailing } : {})}
      {...actions}
    />
  )
}

export function HoldingsBar({
  sessionId,
  projectId,
  onOpenDocument,
  onPutDown,
  onPair,
  onSetMayEdit,
  onUnpair,
  framed,
  leading,
  trailing,
}: {
  sessionId: string
  /** Absent in a context with no project threaded through, where the bar has
   *  no catalog to read titles or staleness from and so does not draw. */
  projectId?: string
  /** Drawn on the bar's line, before and after what it says (RFC 0304 3g: the
   *  compact box puts the session menu and the way into the thread here). */
  leading?: ReactNode
  trailing?: ReactNode
  onOpenDocument?: (documentId: string) => void
  /** Put one down (§4f). Absent leaves the row without the action rather than
   *  with one that fails. */
  onPutDown?: (documentId: string) => void
  /** RFC 0301 §3a: pair this session to a held document, and set or drop it. */
  onPair?: (documentId: string) => void
  onSetMayEdit?: (pairingId: string, mayEdit: boolean) => void
  onUnpair?: (pairingId: string) => void
  /** Draw its own column and padding. Off when a host already gives it one. */
  framed?: boolean
}) {
  const [sessionDoc] = useDocument(sessionId as AnyDocumentId)
  const [projectDoc] = useDocument(projectId as AnyDocumentId | undefined)

  const me = useLocalPersonId()
  // RFC 0345 3g: a protected project keeps no comment index, so its counts
  // read the held documents' own comments, live.
  const repo = useRepo() as unknown as Repo
  const indexless = usesPrivateSessionCatalog(repo)
  const heldIds = useMemo(
    () =>
      indexless
        ? projectSessionHoldings(sessionDoc).map((h) => h.documentId)
        : [],
    [indexless, sessionDoc],
  )
  const held = useCommentMap(projectId, heldIds)
  const rows = useMemo(() => {
    if (!projectId) return []
    const listed = indexless ? held : commentListView(projectDoc)
    return withYourCount(
      projectSessionHeldDocuments(sessionDoc, projectDoc, sessionId, listed),
      projectDoc,
      me,
      listed,
    )
  }, [sessionDoc, projectDoc, projectId, sessionId, me, indexless, held])

  // The session's pairings are listed on its project (RFC 0339 3d) and kept
  // on each document, which is where `mayEdit` is read.
  const pairedIds = useMemo(
    () =>
      sessionPairingRows(projectDoc, sessionId).map(
        (r) => r.documentId as AnyDocumentId,
      ),
    [projectDoc, sessionId],
  )
  const pairedDocs = useDocuments<unknown>(pairedIds)
  const pairings = useMemo(() => {
    const out = new Map<string, Pairing>()
    for (const p of projectPairings(projectDoc, { sessionId })) {
      out.set(p.documentId, p)
    }
    for (const [documentId, doc] of pairedDocs) {
      const p = pairingFor(doc, sessionId, documentId)
      if (p) out.set(documentId, p)
    }
    return out
  }, [projectDoc, pairedDocs, sessionId])

  const { selection, clear } = useSelection()

  return (
    <HoldingsBarView
      rows={rows}
      selection={selection}
      onClearSelection={clear}
      // Once per render rather than per row, so every relative time in one
      // paint agrees with every other.
      now={Date.now()}
      onOpenDocument={onOpenDocument}
      onPutDown={onPutDown}
      pairings={pairings}
      onPair={onPair}
      onSetMayEdit={onSetMayEdit}
      onUnpair={onUnpair}
      {...(framed === undefined ? {} : { framed })}
      {...(leading ? { leading } : {})}
      {...(trailing ? { trailing } : {})}
    />
  )
}

export function HoldingsBarView({
  rows,
  now,
  defaultOpen = false,
  selection = null,
  onClearSelection,
  onOpenDocument,
  onPutDown,
  pairings,
  onPair,
  onSetMayEdit,
  onUnpair,
  framed = true,
  leading,
  trailing,
}: {
  rows: HeldDocument[]
  /** On the line, before and after its own content. With these present the
   *  line is drawn even when nothing is held, because they are the reason. */
  leading?: ReactNode
  trailing?: ReactNode
  now: number
  defaultOpen?: boolean
  /** What is pointed at right now, inside one of these documents (§4e). */
  selection?: Selection | null
  onClearSelection?: () => void
  onOpenDocument?: (documentId: string) => void
  onPutDown?: (documentId: string) => void
  /** This session's pairings, by document id (RFC 0301 §3a). */
  pairings?: Map<string, Pairing>
  onPair?: (documentId: string) => void
  onSetMayEdit?: (pairingId: string, mayEdit: boolean) => void
  onUnpair?: (pairingId: string) => void
  /** False inside the composer, which already sets the column and padding. */
  framed?: boolean
}) {
  const [open, setOpen] = useState(defaultOpen)
  const [all, setAll] = useState(false)

  // NOT an empty bar. A row saying "no documents" is a thing to read, and this
  // is not one. Unless the line carries something else, in which case it is
  // that something's line and the holdings are simply absent from it.
  if (rows.length === 0 && !leading && !trailing) return null

  const needsYou = rows.some((r) => r.changed || r.newComments > 0)
  // A selection inside a document nobody is holding has nowhere to draw. It
  // still exists; the bar simply has no row for it.
  const selectedIn = selection
    ? rows.find((r) => r.documentId === selection.documentId)
    : undefined
  const shown = all ? rows : rows.slice(0, SHOWN)

  return (
    // The same column the composer takes (`max-w-3xl`, centred): the bar is
    // the line above that box, and a line that runs the full width of the
    // window no longer reads as belonging to it.
    <div className={framed ? 'px-3 pt-2' : ''}>
      <div className={framed ? 'mx-auto w-full max-w-3xl' : 'w-full'}>
        {/* The chevron is its own button so the collapsed line can carry the
          selection beside it. A chip nested inside a button is invalid markup
          and stops being clickable in its own right. */}
        <div className="flex w-full items-center gap-2 px-1 pb-1.5 text-muted-foreground text-xs">
          {leading}
          {rows.length > 0 ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              className="flex min-w-0 flex-1 items-center gap-2 text-left transition-colors hover:text-foreground"
            >
              <StatusDot status={needsYou ? 'asking' : 'idle'} />
              <span className="min-w-0 truncate">{summary(rows)}</span>
            </button>
          ) : (
            <span className="min-w-0 flex-1" />
          )}

          {/* COLLAPSED ONLY. Open, the passage is drawn under the document it is
            inside, which says more. Closed, the bar is one line and the one
            thing it must not hide is that the agent is being pointed at
            something. */}
          {!open && selectedIn ? (
            <span
              className="flex min-w-0 max-w-[45%] shrink items-center gap-1 text-muted-foreground/80"
              title={selection?.label}
            >
              <span className="shrink-0 text-[9px] text-muted-foreground/50">
                ▸
              </span>
              <span className="min-w-0 truncate text-[11px] italic">
                {selection?.label}
              </span>
            </span>
          ) : null}

          {rows.length > 0 ? (
            <button
              type="button"
              onClick={() => setOpen((v) => !v)}
              aria-label={
                open ? 'Hide documents in hand' : 'Show documents in hand'
              }
              className="shrink-0 transition-colors hover:text-foreground"
            >
              {open ? (
                <ChevronUp className="size-3 opacity-50" aria-hidden />
              ) : (
                <ChevronDown className="size-3 opacity-50" aria-hidden />
              )}
            </button>
          ) : null}
          {trailing}
        </div>

        {open && rows.length > 0 ? (
          <div className="mb-1.5 overflow-hidden rounded-[var(--radius-project)] border border-border bg-background">
            {shown.map((row) => (
              <HoldingRow
                key={row.documentId}
                row={row}
                now={now}
                selection={
                  selectedIn?.documentId === row.documentId ? selection : null
                }
                onClearSelection={onClearSelection}
                onOpen={onOpenDocument}
                onPutDown={onPutDown}
                pairing={pairings?.get(row.documentId) ?? null}
                onPair={onPair}
                onSetMayEdit={onSetMayEdit}
                onUnpair={onUnpair}
              />
            ))}
            {rows.length > SHOWN && !all ? (
              <button
                type="button"
                onClick={() => setAll(true)}
                className="w-full border-border/60 border-t px-3 py-1.5 text-left text-muted-foreground text-xs hover:text-foreground"
              >
                Show all {rows.length}
              </button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  )
}

function HoldingRow({
  row,
  now,
  selection,
  onClearSelection,
  onOpen,
  onPutDown,
  pairing,
  onPair,
  onSetMayEdit,
  onUnpair,
}: {
  row: HeldDocument
  now: number
  selection?: Selection | null
  onClearSelection?: () => void
  onOpen?: (documentId: string) => void
  onPutDown?: (documentId: string) => void
  pairing?: Pairing | null
  onPair?: (documentId: string) => void
  onSetMayEdit?: (pairingId: string, mayEdit: boolean) => void
  onUnpair?: (pairingId: string) => void
}) {
  return (
    <div className="border-border/60 border-t first:border-t-0">
      <div className="group flex items-center gap-2 px-3 py-1.5 hover:bg-muted/40">
        {/* Deliberate against incidental (§4b): a mention or an open pins, a read
          does not. One glyph rather than a word, because the row is mostly
          title and the distinction is a rank, not a fact you read. */}
        <span
          className={`shrink-0 text-[9px] ${
            row.pinned ? 'text-muted-foreground' : 'text-muted-foreground/30'
          }`}
          title={
            row.pinned ? 'Named or opened deliberately' : 'Read in passing'
          }
        >
          {row.pinned ? '◆' : '·'}
        </span>
        <button
          type="button"
          onClick={() => onOpen?.(row.documentId)}
          title={row.path || row.documentId}
          className="min-w-0 flex-1 truncate text-left text-foreground text-xs hover:underline"
        >
          {row.title}
        </button>

        {row.changed ? (
          <span className="shrink-0 whitespace-nowrap rounded-full bg-amber-500/15 px-2 py-0.5 text-[10px] text-amber-600 dark:text-amber-300">
            changed {ago(row.updatedAt, now)} ago
          </span>
        ) : (
          <span className="shrink-0 whitespace-nowrap text-[10px] text-muted-foreground/70">
            {row.lastKind} {ago(row.lastAt, now)} ago
          </span>
        )}
        {row.newComments > 0 ? (
          // Waiting on YOU (RFC 0324 3f), and a way to it: the count opens the
          // document with the column showing, at the oldest waiting passage.
          <button
            type="button"
            onClick={() => {
              setPendingPassage(
                row.documentId,
                (row as YourHeldDocument).waitingAnchor ?? '',
              )
              onOpen?.(row.documentId)
            }}
            className="shrink-0 whitespace-nowrap rounded-full bg-yellow-500 px-2 py-0.5 font-semibold text-[10px] text-black"
          >
            {row.newComments} waiting on you
          </button>
        ) : null}

        {/* RFC 0301 §3a. A pairing is always shown, because a session that
          may act on this document unasked is the one thing on the row you
          must not have to hover to find. The toggle is the only knob. */}
        {pairing ? (
          <span className="flex shrink-0 items-center gap-1.5 whitespace-nowrap text-[10px] text-muted-foreground">
            <span>paired</span>
            <span aria-hidden>·</span>
            <button
              type="button"
              role="switch"
              aria-checked={pairing.mayEdit}
              disabled={!onSetMayEdit}
              onClick={() => onSetMayEdit?.(pairing.id, !pairing.mayEdit)}
              className="flex items-center gap-1.5 hover:text-foreground"
            >
              <span
                className={`relative inline-block h-3 w-5 shrink-0 rounded-full transition-colors ${
                  pairing.mayEdit
                    ? 'bg-foreground/70'
                    : 'bg-muted-foreground/30'
                }`}
                aria-hidden
              >
                <span
                  className={`absolute top-[2px] left-0 h-2 w-2 rounded-full bg-background transition-transform ${
                    pairing.mayEdit ? 'translate-x-[10px]' : 'translate-x-[2px]'
                  }`}
                />
              </span>
              may edit
            </button>
            {onUnpair ? (
              <button
                type="button"
                onClick={() => onUnpair(pairing.id)}
                className="rounded px-1 opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
              >
                unpair
              </button>
            ) : null}
          </span>
        ) : onPair ? (
          <button
            type="button"
            onClick={() => onPair(row.documentId)}
            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
          >
            pair
          </button>
        ) : null}

        {/* On hover only: a permanent row of buttons over every holding is
          furniture, and a row is read far more often than it is acted on. */}
        {onPutDown ? (
          <button
            type="button"
            onClick={() => onPutDown(row.documentId)}
            className="shrink-0 rounded px-1.5 py-0.5 text-[10px] text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:opacity-100 group-hover:opacity-100"
          >
            put down
          </button>
        ) : null}
      </div>

      {/* UNDER its document, not in a list of its own (§4e). A selection is a
          part of something already held, and showing it anywhere else would be
          a second list of things pointing at nothing. */}
      {selection ? (
        <div className="flex items-start gap-2 px-3 pb-1.5 pl-8">
          <span className="mt-[3px] shrink-0 text-[9px] text-muted-foreground/50">
            ▸
          </span>
          <span className="min-w-0 flex-1 text-[11px] text-muted-foreground italic">
            {selection.label}
          </span>
          {onClearSelection ? (
            <button
              type="button"
              onClick={onClearSelection}
              aria-label="Clear selection"
              className="shrink-0 rounded px-1 text-[11px] text-muted-foreground/60 hover:text-foreground"
            >
              ×
            </button>
          ) : null}
        </div>
      ) : null}
    </div>
  )
}

/**
 * The bar is drawn for a person, so it counts what is waiting on the person
 * (RFC 0324 3f): open threads on each document whose last comment is somebody
 * else's. The session's own unread count stays in read_document's footer,
 * which is where an agent reads it.
 */
export function withYourCount(
  rows: HeldDocument[],
  projectDoc: unknown,
  personId: string | null,
  listed = commentListView(projectDoc),
): YourHeldDocument[] {
  return rows.map((r) => {
    // Off the project's index (RFC 0339 3d): who wrote last, whether it is
    // resolved and its passage are all the count needs.
    const waiting = projectThreads(listed, {
      documentId: r.documentId,
    })
      .filter((t) => isWaitingOn(t, personId))
      .sort((a, b) => a.root.createdAt - b.root.createdAt)
    const anchor = waiting.find((t) => t.root.target.anchorText)?.root.target
      .anchorText
    return {
      ...r,
      newComments: waiting.length,
      // The OLDEST waiting thread with a passage: the one you have left
      // longest, and the one the count opens at (RFC 0324 3f).
      ...(anchor ? { waitingAnchor: String(anchor) } : {}),
    }
  })
}

/** A held document as the bar draws it for a person. */
export type YourHeldDocument = HeldDocument & {
  /** The passage of the oldest thread waiting on you, when it has one. */
  waitingAnchor?: string
}
